import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | timeout
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("timeout"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
      } catch {}
      if (attempts.current >= 8) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId, refresh]);

  return (
    <div className="pt-24 min-h-screen flex items-center justify-center px-6">
      <div className="qs-card p-10 max-w-md w-full text-center" data-testid="payment-success-page">
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto text-cyan-400 animate-spin mb-4" size={40} />
            <h1 className="font-display text-2xl font-bold">Confirming your payment…</h1>
            <p className="text-slate-400 mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-400 mb-4" size={48} />
            <h1 className="font-display text-2xl font-bold">You're in! 🎉</h1>
            <p className="text-slate-400 mt-2">Your subscription is active. Gold & Crypto AI signals are now unlocked.</p>
            <Link to="/signals">
              <Button data-testid="success-dashboard-button" className="mt-6 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full h-11 px-6">Go to Signal Desk</Button>
            </Link>
          </>
        )}
        {status === "timeout" && (
          <>
            <Loader2 className="mx-auto text-amber-400 mb-4" size={40} />
            <h1 className="font-display text-2xl font-bold">Still processing</h1>
            <p className="text-slate-400 mt-2">Your payment is taking a little longer. Refresh the dashboard shortly.</p>
            <Link to="/signals">
              <Button className="mt-6 bg-slate-800 hover:bg-slate-700 text-white rounded-full h-11 px-6">Back to Dashboard</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const nav = useNavigate();
  return (
    <div className="pt-24 min-h-screen flex items-center justify-center px-6">
      <div className="qs-card p-10 max-w-md w-full text-center" data-testid="payment-cancel-page">
        <XCircle className="mx-auto text-red-400 mb-4" size={48} />
        <h1 className="font-display text-2xl font-bold">Checkout cancelled</h1>
        <p className="text-slate-400 mt-2">No charge was made. You can upgrade anytime.</p>
        <Button onClick={() => nav("/pricing")} className="mt-6 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full h-11 px-6">View Plans Again</Button>
      </div>
    </div>
  );
}
